import { useCallback, useState } from 'react';

import { CheckIcon, ChevronDownIcon, LanguagesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  isLoading: boolean;
  isButtonSelected: boolean;
  onButtonClick: () => void;
  onValueSelect: (value: string) => void;
}

const TranslateSelect = ({
  options,
  value,
  isLoading,
  isButtonSelected,
  onButtonClick,
  onValueSelect,
}: ComboboxProps) => {
  const { t } = useTranslation();
  const [comboboxOpen, setComboboxOpen] = useState(false);

  const handleButtonClick = useCallback(() => {
    // No language picked yet → open the dropdown so the user can choose one.
    if (!value) {
      setComboboxOpen(true);
      return;
    }
    onButtonClick();
  }, [onButtonClick, value]);

  const handleValueSelect = useCallback(
    (newValue: string) => {
      onValueSelect(newValue);
      setComboboxOpen(false);
    },
    [onValueSelect]
  );

  return (
    // Add a relative parent container to establish a positioning context for the icon button.
    // 'w-fit' ensures the container only takes up the necessary width.
    <div className="relative w-fit">
      <Popover modal open={comboboxOpen} onOpenChange={setComboboxOpen}>
        <InfoTooltip
          content={
            isButtonSelected
              ? t('try_it.show_original_tooltip')
              : t('try_it.show_translation_tooltip')
          }
          side="bottom"
          size="auto"
          className="z-3000"
        >
          <Button
            size="icon-md"
            variant="ghost-neutral"
            className={cn(
              'absolute top-1/2 left-1 z-10 h-8 w-8 -translate-y-1/2 bg-background shadow-sm',
              isButtonSelected && 'border border-primary bg-bg-info-soft [&_svg]:text-primary'
            )}
            onClick={handleButtonClick}
          >
            <LanguagesIcon size={20} />
          </Button>
        </InfoTooltip>
        <InfoTooltip content={t('try_it.translation_language')} className="z-3000">
          <PopoverTrigger asChild>
            <Button
              variant="ghost-neutral"
              role="combobox"
              className={cn('w-18.5 min-w-0 justify-end border bg-bg-muted-light pr-3')}
            >
              {isLoading ? (
                <Spinner className="shrink-0" />
              ) : (
                <ChevronDownIcon size={16} className="shrink-0" />
              )}
            </Button>
          </PopoverTrigger>
        </InfoTooltip>

        {/* Z-INDEX is 3000 to be above a modal */}
        <PopoverContent className="z-3000 w-60 p-0">
          <Command>
            <CommandInput placeholder={t('common.search')} />
            <CommandList>
              <CommandGroup>
                <ScrollArea className="h-40">
                  {options
                    .sort((a, b) => (b.value === value ? 1 : 0) - (a.value === value ? 1 : 0))
                    .map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={() => {
                          handleValueSelect(option.value === value ? '' : option.value);
                        }}
                        className="flex justify-between"
                      >
                        {option.label}
                        <CheckIcon
                          className={cn(
                            'h-4 w-4',
                            value && value === option.value ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    ))}
                </ScrollArea>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TranslateSelect;
